"use client";

import { useState, useEffect } from "react";
import {
  Settings,
  Building,
  Users,
  ChevronRight,
  Layers,
  Briefcase,
  UserPlus,
} from "lucide-react";
import "./SettingsPage.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BackButton from "../shared/backButton";
import {
  readPartnerInviteUnread,
  subscribePartnerInviteUnread,
} from "../shared/utils/partnerInviteNotificationState";

const SettingsPage = () => {
  const { t } = useTranslation();
  const [animateCards, setAnimateCards] = useState(false);
  const [hasUnreadPartnerInvite, setHasUnreadPartnerInvite] = useState(
    readPartnerInviteUnread,
  );

  useEffect(() => {
    // Trigger animation after component mounts
    setAnimateCards(true);
  }, []);

  useEffect(() => {
    return subscribePartnerInviteUnread(
      setHasUnreadPartnerInvite,
    );
  }, []);

  const navigate = useNavigate();
  const onNavigate = (page) => {
    console.log("Navigating to:", page);
    navigate(page);
  };

  const settingsCards = [
    {
      id: "organization",
      title: t("settingsPage.organizationInfo"),
      description: t("organizationInfo.pageDescription"),
      icon: Building,
      color: "#122d44",
      items: [
        t("settingsPage.organizationProfile"),
        t("settingsPage.departments"),
        t("settingsPage.businessUnits"),
        t("settingsPage.coreSoftSkills"),
      ],
    },
    {
      id: "onboard-organization",
      title: t("settingsPage.onboardOrganization"),
      description: t("settingsPage.onboardOrganizationDescription"),
      icon: UserPlus,
      color: "#122d44",
      items: [
        t("settingsPage.organizationManagement"),
        t("settingsPage.onboardNewOrganizations"),
        t("settingsPage.organizationProfiles"),
        t("settingsPage.partnershipManagement"),
      ],
    },
    {
      id: "users",
      title: t("settingsPage.users"),
      description: t("settingsPage.userManagement"),
      icon: Users,
      color: "#5b161a",
      items: [
        t("settingsPage.userManagement"),
        t("settingsPage.onboardNewUsers"),
        t("settingsPage.rolePermissions"),
        t("settingsPage.accessControl"),
      ],
    },
    {
      id: "job-roles",
      title: t("settingsPage.jobRoles"),
      description: t("jobRoles.pageDescription"),
      icon: Briefcase,
      color: "#5b161a",
      items: [
        t("settingsPage.jobRoleManagement"),
        t("settingsPage.roleDefinitions"),
        t("settingsPage.skillRequirements"),
        t("settingsPage.roleTemplates"),
      ],
    },
  ];

  {
    /*
      id: "teams",
      title: t("settingsPage.teams"),
      description: t("teamsPage.pageDescription"),
      icon: Layers,
      color: "#122d44",
      items: [
        t("settingsPage.teamManagement"),
        t("settingsPage.teamStructures"),
        t("settingsPage.teamRoles"),
        t("settingsPage.teamPermissions"),
      ],
    */
  }

  const handleCardClick = (cardId) => {
    switch (cardId) {
      case "organization":
        onNavigate("organization-info");
        break;
      case "users":
        onNavigate("users");
        break;
      case "onboard-organization":
        onNavigate("onboard-organization");
        break;
      case "job-roles":
        onNavigate("/job-roles");
        break;
      case "teams":
        onNavigate("/teams");
        break;
      default:
        break;
    }
  };

  return (
    <div className="settings-page">
      <main className="settings-main">
        <div className="container">
          <div className="page-back-button">
            <BackButton />
          </div>
          <div className="page-header">
            <div className="header-icon">
              <Settings size={32} />
            </div>
            <h1>{t("settingsPage.pageTitle")}</h1>
            <p>{t("settingsPage.pageDescription")}</p>
          </div>

          <div
            className={`settings-cards-updated ${
              animateCards ? "animate" : ""
            }`}
          >
            {/* First row - 3 cards */}
            <div className="settings-row-first">
              {settingsCards.slice(0, 2).map((card, index) => (
                <div
                  key={card.id}
                  className="settings-card"
                  onClick={() => handleCardClick(card.id)}
                  style={{
                    animationDelay: `${index * 0.1}s`,
                    borderTop: `4px solid ${card.color}`,
                  }}
                >
                  <div className="card-icon">
                    <card.icon size={24} />
                  </div>
                  {card.id === "organization" && hasUnreadPartnerInvite && (
                    <span
                      className="settings-partner-notification-dot"
                      aria-label="Pending partner invitation"
                    />
                  )}
                  <div className="card-content">
                    <h2>{card.title}</h2>
                    <p>{card.description}</p>
                  </div>
                  <div className="card-action">
                    <ChevronRight size={20} />
                  </div>
                </div>
              ))}
            </div>

            {/* Second row - 2 cards centered */}
            <div className="settings-row-second">
              {settingsCards.slice(2, 4).map((card, index) => (
                <div
                  key={card.id}
                  className="settings-card"
                  onClick={() => handleCardClick(card.id)}
                  style={{
                    animationDelay: `${(index + 3) * 0.1}s`,
                    borderTop: `4px solid ${card.color}`,
                  }}
                >
                  <div className="card-icon">
                    <card.icon size={24} />
                  </div>
                  <div className="card-content">
                    <h2>{card.title}</h2>
                    <p>{card.description}</p>
                  </div>
                  <div className="card-action">
                    <ChevronRight size={20} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SettingsPage;
